// src/features/auth/security/storage/EncryptionService.ts

import { EncryptionAlgorithm, EncryptionResult } from '../../types/security.types';

export interface EncryptionConfig {
  algorithm?: EncryptionAlgorithm;
  keyLength?: number;
  ivLength?: number;
  tagLength?: number;
}

export class EncryptionService {
  private config: Required<EncryptionConfig>;

  constructor(config: EncryptionConfig = {}) {
    this.config = {
      algorithm: EncryptionAlgorithm.AES_GCM,
      keyLength: 256,
      ivLength: 12,
      tagLength: 128,
      ...config,
    };
  }

  /**
   * Encrypt data
   */
  async encrypt(data: string, key: CryptoKey): Promise<EncryptionResult> {
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    
    // Generate IV
    const iv = crypto.getRandomValues(new Uint8Array(this.config.ivLength));
    
    // Encrypt
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: this.config.algorithm,
        iv,
        tagLength: this.config.tagLength,
      },
      key,
      encodedData
    );

    return {
      ciphertext: this.arrayBufferToBase64(encryptedData),
      iv: this.arrayBufferToBase64(iv),
      algorithm: this.config.algorithm,
    };
  }

  /**
   * Decrypt data
   */
  async decrypt(encryptedData: EncryptionResult, key: CryptoKey): Promise<string> {
    const ciphertext = this.base64ToArrayBuffer(encryptedData.ciphertext);
    const iv = this.base64ToArrayBuffer(encryptedData.iv);

    const decryptedData = await crypto.subtle.decrypt(
      {
        name: encryptedData.algorithm,
        iv,
        tagLength: this.config.tagLength,
      },
      key,
      ciphertext
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  }

  /**
   * Generate encryption key
   */
  async generateKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(
      {
        name: this.config.algorithm,
        length: this.config.keyLength,
      },
      true,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Import key from raw bytes
   */
  async importKey(keyData: ArrayBuffer): Promise<CryptoKey> {
    return crypto.subtle.importKey(
      'raw',
      keyData,
      {
        name: this.config.algorithm,
        length: this.config.keyLength,
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Export key to raw bytes
   */
  async exportKey(key: CryptoKey): Promise<ArrayBuffer> {
    return crypto.subtle.exportKey('raw', key);
  }

  /**
   * Convert ArrayBuffer to base64
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64 to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
